import { useEffect, useState, RefObject } from 'react';

export interface AutocompleteResult {
    latitude: number;
    longitude: number;
    formattedAddress: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
}

const googleApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_API_KEY';

export function useGoogleMapsScript() {
    const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);

    useEffect(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
            setGoogleScriptLoaded(true);
            return;
        }

        const existingScript = document.getElementById('google-maps-sdk-script');
        if (!existingScript) {
            const script = document.createElement('script');
            script.id = 'google-maps-sdk-script';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => setGoogleScriptLoaded(true);
            document.head.appendChild(script);
        } else {
            existingScript.addEventListener('load', () => setGoogleScriptLoaded(true));
        }
    }, []);

    return googleScriptLoaded;
}

// Attaches a Google Places Autocomplete widget to `inputRef` while `enabled` is true.
export function useAddressAutocomplete(
    inputRef: RefObject<HTMLInputElement | null>,
    enabled: boolean,
    onPlaceSelected: (result: AutocompleteResult) => void
) {
    const googleScriptLoaded = useGoogleMapsScript();

    useEffect(() => {
        if (!googleScriptLoaded || !window.google) return;
        let mainAutocompleteWidget: any = null;

        if (enabled && inputRef.current) {
            mainAutocompleteWidget = new window.google.maps.places.Autocomplete(inputRef.current, {
                componentRestrictions: { country: 'us' },
                types: ['address'],
                fields: ['address_components', 'geometry', 'formatted_address']
            });

            mainAutocompleteWidget.addListener('place_changed', () => {
                const selectedPlace = mainAutocompleteWidget.getPlace();
                if (!selectedPlace.geometry) return;

                let streetNumber = '', routeName = '', localityCity = '', stateShort = '', postalCode = '';
                for (const component of selectedPlace.address_components) {
                    if (component.types.includes('street_number')) streetNumber = component.long_name;
                    if (component.types.includes('route')) routeName = component.long_name;
                    if (component.types.includes('locality')) localityCity = component.long_name;
                    if (component.types.includes('administrative_area_level_1')) stateShort = component.short_name;
                    if (component.types.includes('postal_code')) postalCode = component.long_name;
                }

                onPlaceSelected({
                    latitude: selectedPlace.geometry.location.lat(),
                    longitude: selectedPlace.geometry.location.lng(),
                    formattedAddress: selectedPlace.formatted_address || '',
                    streetAddress: `${streetNumber} ${routeName}`.trim(),
                    city: localityCity,
                    state: stateShort,
                    zipCode: postalCode,
                });
            });
        }

        return () => {
            if (window.google?.maps?.event && mainAutocompleteWidget) {
                window.google.maps.event.clearInstanceListeners(mainAutocompleteWidget);
            }
        };
        // onPlaceSelected only wraps stable setState setters in both call sites, so it's
        // intentionally excluded to match the original effect's [scriptLoaded, enabled] deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleScriptLoaded, enabled]);

    return googleScriptLoaded;
}
